import { AlertCircle, ChevronLeft, ChevronRight, Package, Plus } from "lucide-react";

import type { Pro014StepFourData } from "@/@types/campaign";
import type { CampaignConflictItem } from "@/@types/campaign-conflict";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { GradeRestrictionSwitch } from "../../../components/grade/grade-restriction-switch";
import { GradeTargetModal } from "../../../components/grade/grade-target-modal";
import { ItemsWithoutGradeAlert } from "../../../components/grade/items-without-grade-alert";
import {
  CONFLICT_FILTER,
  PRO014_MIN_ITEMS,
  PRO014_PRODUCTS_TEXTS,
  PRO014_READ_ONLY_STATUS,
  PRO014_WIZARD_TEXTS,
} from "../../constants";
import { usePro014ProductsStep } from "../../hooks/use-pro014-products-step";
import type { ConflictFilter, Pro014ProductsStep } from "../../types";
import { ExcelUploadPreview } from "../shared/excel-upload-preview";
import { ProductsExcelImport } from "./products-excel-import";
import { ProductsList } from "./products-list";

const SKU_INPUT_ID = "itemCode";

const READ_ONLY_MESSAGES: Record<string, string> = {
  [PRO014_READ_ONLY_STATUS.EXPIRED]: PRO014_PRODUCTS_TEXTS.EXPIRED,
  [PRO014_READ_ONLY_STATUS.CANCELLED]: PRO014_PRODUCTS_TEXTS.CANCELLED,
};

interface StepFourProps {
  data: Pro014StepFourData;
  updateData: (data: Partial<Pro014StepFourData>) => void;
  nextStep: () => void;
  prevStep: () => void;
  campaignStatus?: string;
  conflicts?: CampaignConflictItem[];
  conflictFilter?: ConflictFilter;
  onConflictFilterChange?: (filter: ConflictFilter) => void;
  onRemoveFromPrevious?: (conflict: CampaignConflictItem) => void;
  leavesTooFewActive?: boolean;
}

export default function StepFour({
  data,
  updateData,
  nextStep,
  prevStep,
  campaignStatus,
  conflicts = [],
  conflictFilter = CONFLICT_FILTER.ALL,
  onConflictFilterChange,
  onRemoveFromPrevious,
  leavesTooFewActive,
}: StepFourProps): JSX.Element {
  const readOnlyMessage: string | undefined = campaignStatus
    ? READ_ONLY_MESSAGES[campaignStatus]
    : undefined;
  const isReadOnly: boolean = Boolean(readOnlyMessage);
  const step: Pro014ProductsStep = usePro014ProductsStep({
    data,
    updateData,
    nextStep,
    conflicts,
    conflictFilter,
    onConflictFilterChange,
    leavesTooFewActive,
  });

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" aria-hidden />
          <div>
            <h3 className="text-base font-semibold">{PRO014_PRODUCTS_TEXTS.TITLE}</h3>
            <p className="text-sm text-muted-foreground">
              {PRO014_PRODUCTS_TEXTS.SUBTITLE(PRO014_MIN_ITEMS)}
            </p>
          </div>
        </div>

        <GradeRestrictionSwitch
          checked={data.gradeRestricted}
          onCheckedChange={step.toggleRestriction}
          disabled={isReadOnly}
        />

        {readOnlyMessage && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-600" aria-hidden />
              <p className="text-sm font-medium text-yellow-800">{readOnlyMessage}</p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor={SKU_INPUT_ID} className="text-sm font-medium">
            {PRO014_PRODUCTS_TEXTS.SKU_LABEL}
          </Label>
          <div className="flex gap-2">
            <Input
              id={SKU_INPUT_ID}
              value={step.itemCode}
              disabled={isReadOnly}
              onChange={(event) => step.changeItemCode(event.target.value)}
              onKeyDown={step.handleItemCodeKey}
              placeholder={PRO014_PRODUCTS_TEXTS.SKU_PLACEHOLDER}
              className="h-9 flex-1"
            />
            <Button type="button" onClick={step.addItem} disabled={isReadOnly} className="h-9">
              <Plus className="mr-1 h-4 w-4" aria-hidden />
              {PRO014_PRODUCTS_TEXTS.ADD}
            </Button>
          </div>
          {step.errors.itemCode && (
            <p className="flex items-center gap-1 text-sm text-destructive">
              <AlertCircle className="h-3 w-3" aria-hidden />
              {step.errors.itemCode}
            </p>
          )}
          <p className="text-xs text-muted-foreground">{PRO014_PRODUCTS_TEXTS.ADD_HINT}</p>
        </div>

        <ProductsExcelImport
          fileInputRef={step.fileInputRef}
          uploadProgress={step.uploadProgress}
          isProcessing={step.isProcessing}
          disabled={isReadOnly}
          onDownloadTemplate={step.downloadTemplate}
          onFileUpload={step.handleFileUpload}
        />

        <ProductsList
          totalItems={data.items.length}
          pageItems={step.pageItems}
          visibleCount={step.visibleCount}
          pageOffset={step.pageOffset}
          currentPage={step.currentPage}
          totalPages={step.totalPages}
          conflictByCode={step.conflictByCode}
          hasConflicts={conflicts.length > 0}
          conflictFilter={conflictFilter}
          searchTerm={step.searchTerm}
          gradeRestricted={data.gradeRestricted}
          isReadOnly={isReadOnly}
          itemsError={step.errors.items}
          onSearchChange={step.changeSearch}
          onConflictFilterChange={step.changeConflictFilter}
          onGoToPage={step.goToPage}
          onRemoveConflicting={step.removeConflictingItems}
          onRemoveItem={step.removeItem}
          onEditGrade={step.openGrade}
          onRemoveFromPrevious={onRemoveFromPrevious}
        />

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950/20">
          <div className="flex gap-2">
            <AlertCircle
              className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400"
              aria-hidden
            />
            <div>
              <p className="mb-1 text-sm font-medium text-blue-900 dark:text-blue-300">
                {PRO014_PRODUCTS_TEXTS.VALIDATION_TITLE}
              </p>
              <ul className="list-inside list-disc space-y-1 text-xs text-blue-700 dark:text-blue-400">
                {PRO014_PRODUCTS_TEXTS.VALIDATION_RULES.map((rule: string) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <ItemsWithoutGradeAlert itemCodes={step.itemsWithoutGrade} />

      <div className="flex justify-between gap-2 pt-2">
        <Button variant="outline" onClick={prevStep} className="w-full md:w-auto">
          <ChevronLeft className="mr-2 h-4 w-4" aria-hidden />
          {PRO014_WIZARD_TEXTS.BACK}
        </Button>
        <Button
          onClick={step.continueToReview}
          disabled={!step.canContinue}
          className="w-full md:w-auto"
        >
          {PRO014_WIZARD_TEXTS.CONTINUE}
          <ChevronRight className="ml-2 h-4 w-4" aria-hidden />
        </Button>
      </div>

      {step.gradeItem && (
        <GradeTargetModal
          isOpen
          itemCode={step.gradeItem.sku}
          itemName={step.gradeItem.name}
          initialTargets={step.gradeItem.grades ?? []}
          onConfirm={step.confirmGrades}
          onClose={step.closeGrade}
        />
      )}

      {step.preview && (
        <ExcelUploadPreview
          isOpen
          onClose={step.closePreview}
          onConfirm={step.confirmImport}
          validItems={step.preview.validItems}
          invalidSkus={step.preview.invalidSkus}
          duplicateSkus={step.preview.duplicateSkus}
        />
      )}
    </div>
  );
}
